import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { watchDebounced } from "@vueuse/core";
import { useAppToast } from "@/shared/composables/useAppToast";
import { logger } from "@/infrastructure/services/LoggerService";
import { getSubscriptionViews, saveSubscriptionViews, } from "../../infrastructure/adapters/SubscriptionViewPreferencesService";
import { buildPresetQuerySnapshot, filterValueLabelKey } from "./subscriptionViewPresets";
const SAVE_DEBOUNCE_MS = 500;
const SCHEMA_VERSION = 1;
const DEFAULT_VIEW_ID = "all";
/** Unions several filter maps by key (OR within a key — multi-value IN). */
function mergeFilters(maps) {
    const out = {};
    for (const map of maps) {
        for (const [key, filter] of Object.entries(map)) {
            const current = out[key] ?? { type: "in", values: [] };
            for (const value of filter.values) {
                if (!current.values.includes(value))
                    current.values.push(value);
            }
            out[key] = current;
        }
    }
    return out;
}
/** Deep-clones a filter map, keeping only the `in`-type `{ type, values }` shape (detaches reactivity). */
function cloneFilters(filters) {
    const out = {};
    for (const [key, filter] of Object.entries(filters)) {
        if (filter?.type === "in" && Array.isArray(filter.values) && filter.values.length > 0) {
            out[key] = { type: "in", values: [...filter.values] };
        }
    }
    return out;
}
/**
 * Persists and restores the subscriptions-page custom prefiltered lists (OP-6176).
 *
 * A list stores the filter state it applies. Lists are created by combining selected Eudonet preset
 * filters and/or existing lists ({@link createListFromSelection}), or by saving the current filter
 * state ({@link saveCurrentView}). They become tabs, can be renamed/deleted, and are persisted to
 * OPREF + restored on reopen. Mirrors {@link useDonationViewPreferences}.
 */
export function useSubscriptionViewPreferences(options) {
    const { tableQuery, tableViews, hasUrlView } = options;
    const { t } = useI18n();
    const { toastSuccess, toastError } = useAppToast();
    const savedViews = ref([]);
    let restored = false;
    let lastSavedJson = "";
    /** Human label from a filter map, e.g. "Actif + Mensuel". */
    function labelFromFilters(filters) {
        const parts = [];
        for (const [key, filter] of Object.entries(filters)) {
            for (const value of filter.values) {
                parts.push(t(filterValueLabelKey(key, value)));
            }
        }
        return parts.join(" + ");
    }
    function currentPayload() {
        return {
            version: SCHEMA_VERSION,
            savedViews: savedViews.value.map((v) => ({ id: v.id, label: v.label, filters: cloneFilters(v.filters) })),
            activeViewId: tableQuery.state.activeViewId ?? null,
        };
    }
    /**
     * Adds a saved list as a tab carrying a CLONE of its filter snapshot (idempotent on id).
     * Cloning decouples the OPAL view's querySnapshot from the saved list's stored `filters`, so
     * editing the live filters never mutates (and silently re-persists) the saved list.
     */
    function addAsTab(view) {
        if (tableViews.views.value.find((v) => v.id === view.id))
            return;
        tableViews.addView({
            id: view.id,
            label: view.label,
            isPreset: true,
            querySnapshot: { filters: cloneFilters(view.filters) },
        });
    }
    /** Creates a new list from a filter map, activates it, and persists. No-op when empty. */
    function createListFromFilters(filters) {
        if (Object.keys(filters).length === 0)
            return;
        const view = {
            id: crypto.randomUUID(),
            label: labelFromFilters(filters),
            filters,
        };
        savedViews.value.push(view);
        addAsTab(view);
        tableQuery.setView(view.id);
        toastSuccess("finance.subscriptions.views.toastCreated");
    }
    /**
     * Creates a list by combining the selected dropdown items: Eudonet preset ids contribute their
     * preset filter, selected existing lists contribute their stored filters; all are merged
     * (status IN […] etc.) into one new list.
     */
    function createListFromSelection(selectedIds) {
        const presetFilters = buildPresetQuerySnapshot(selectedIds).filters;
        const listFilters = selectedIds
            .map((id) => savedViews.value.find((v) => v.id === id)?.filters)
            .filter((f) => !!f)
            .map(cloneFilters);
        createListFromFilters(mergeFilters([presetFilters, ...listFilters]));
    }
    /**
     * "Enregistrer" — saves the current table filter state. When the active tab is an existing custom
     * list, its filters are UPDATED in place (keeping its name); otherwise (a built-in preset tab) a
     * new list is created.
     */
    function saveCurrentView(currentFilters) {
        const filters = cloneFilters(currentFilters);
        if (Object.keys(filters).length === 0)
            return;
        const activeId = tableQuery.state.activeViewId;
        const existing = activeId ? savedViews.value.find((v) => v.id === activeId) : undefined;
        if (existing) {
            existing.filters = filters;
            tableViews.updateView(existing.id, { querySnapshot: { filters } });
            toastSuccess("finance.subscriptions.views.toastUpdated");
            return;
        }
        createListFromFilters(filters);
    }
    /** Renames an existing custom list. */
    function renameList(id, label) {
        const trimmed = label.trim();
        if (!trimmed)
            return;
        const view = savedViews.value.find((v) => v.id === id);
        if (!view)
            return;
        view.label = trimmed;
        tableViews.updateView(id, { label: trimmed });
        toastSuccess("finance.subscriptions.views.toastRenamed");
    }
    /** Deletes a custom list; if it was active, falls back to the default "all" tab. */
    function deleteList(id) {
        const index = savedViews.value.findIndex((v) => v.id === id);
        if (index === -1)
            return;
        savedViews.value.splice(index, 1);
        tableViews.removeView(id);
        if (tableQuery.state.activeViewId === id) {
            tableQuery.setView(DEFAULT_VIEW_ID);
        }
        toastSuccess("finance.subscriptions.views.toastDeleted");
    }
    /**
     * Loads persisted lists and restores them as tabs, then — unless the URL already selected a
     * view — activates the persisted tab. Safe to call once on mount.
     */
    async function restore() {
        try {
            const prefs = await getSubscriptionViews();
            if (prefs) {
                for (const view of prefs.savedViews) {
                    const filters = cloneFilters(view.filters ?? {});
                    // Self-heal: drop lists that resolve to no filters (corrupted/empty data). They can't
                    // filter anything; the next save prunes them from storage.
                    if (Object.keys(filters).length === 0)
                        continue;
                    const restoredView = { id: view.id, label: view.label, filters };
                    savedViews.value.push(restoredView);
                    addAsTab(restoredView);
                }
                const canActivate = !hasUrlView() &&
                    !!prefs.activeViewId &&
                    tableViews.views.value.some((v) => v.id === prefs.activeViewId);
                if (canActivate) {
                    tableQuery.setView(prefs.activeViewId);
                }
            }
        }
        catch (error) {
            logger.error("Failed to load subscription view preferences", error);
        }
        finally {
            restored = true;
            lastSavedJson = JSON.stringify(currentPayload());
        }
    }
    async function persist() {
        const payload = currentPayload();
        const json = JSON.stringify(payload);
        if (json === lastSavedJson)
            return;
        try {
            await saveSubscriptionViews(payload);
            lastSavedJson = json;
        }
        catch (error) {
            logger.error("Failed to save subscription view preferences", error);
            // useAppToast translates the key itself — pass the key, not t(key).
            toastError("finance.subscriptions.views.toastSaveError");
        }
    }
    watchDebounced(() => [JSON.stringify(savedViews.value), tableQuery.state.activeViewId], () => {
        if (!restored)
            return;
        void persist();
    }, { debounce: SAVE_DEBOUNCE_MS });
    return { savedViews, createListFromSelection, saveCurrentView, renameList, deleteList, restore };
}
